import { useCallback, useContext, useEffect } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { EmptyArray } from 'lib/array/arrayUtils'
import checkPersonalisedSaving from 'luxPlus/lib/checkPersonalisedSaving'
import styled from 'styled-components'
import { rem } from 'polished'
import { useInView } from 'react-intersection-observer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import Pane from 'components/Common/Pane'
import Sheet from 'components/utils/Sheet'
import Group from 'components/utils/Group'
import AccountAccessModal from 'components/Account/AccountAccess/AccountAccessModal'
import type { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal.types'
import ModalContext from 'contexts/ModalContext'
import GeoContext from 'contexts/geoContext'
import BodyText from 'LuxKit/Typography/BodyText'
import TextLink from 'LuxKit/TextLink'
import Caption from 'LuxKit/Typography/Caption'
import HeadingTextBlock from 'LuxKit/TextBlocks/HeadingTextBlock'
import LuxPlusLogo from '../Logos/LuxPlusLogo'
import LuxPlusStatCards from './LuxPlusStatCards'
import LuxPlusPromoBannerCarousel from './LuxPlusPromoBannerCarousel'
import LuxPlusLandingAboutSection from 'luxPlus/components/LandingPage/LuxPlusLandingAboutSection'
import LuxPlusLandingBenefitsSection from './LuxPlusLandingBenefitsSection'
import LuxPlusLandingPartnerOffersTabSection from './LuxPlusLandingPartnerOffersTabSection'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getRegionSaveAmount } from 'luxPlus/lib/getRegionSaveAmountCopy'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import zIndex from 'styles/tools/z-index'
import { useLuxPlusPromoBanners } from 'luxPlus/hooks/useLuxPlusPromos'
import usePartnerPromoOffers from 'luxPlus/hooks/usePartnerPromoOffers'
import useLuxPlusLandingTab from 'luxPlus/hooks/useLuxPlusLandingTab'
import usePotentialSavingsRows from 'luxPlus/hooks/usePotentialSavingsRows'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { selectLoggedIn } from 'selectors/accountSelectors'
import { fetchTotalPotentialSavings } from 'actions/LuxPlusActions'
import LuxPlusPotentialSavingsModal from '../modals/LuxPlusPotentialSavingsModal'
import LuxPlusLandingNavigationProvider from 'luxPlus/contexts/LuxPlusLandingNavigationProvider'
import { type LuxPlusLandingNonMemberTab } from 'luxPlus/contexts/LuxPlusLandingNavigationContext'
import SegmentedOption from 'LuxKit/SegmentedOptions/SegmentedOption'
import type { SegmentedOptionsGroupOnValueChangeHandler } from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import SegmentedOptionsGroup from 'LuxKit/SegmentedOptions/SegmentedOptionsGroup'
import variables from 'styles/vendor/bootstrap/variables'
import { scrollToId } from 'lib/window/scrollUtils'
import LuxPlusLandingCtaButtons from './LuxPlusLandingCtaButtons'
import { getThemeClassName } from 'lib/theme/themeUtils'
import useRebrandVersion from 'hooks/useRebrandVersion'

const CTAContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${rem(12)};
  margin-bottom: ${rem(16)};
`

const HeroGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: ${rem(32)};
  align-items: center;

  ${mediaQueryUp.desktop} {
    grid-template-columns: 1fr 1fr;
  }

`

const HeroLayoutContainer = styled(LayoutContainer)`
  padding-inline: 0;

  ${mediaQueryUp.desktop} {
    padding-inline: ${rem(variables.gutterWidth / 2)};
  }
`

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${rem(16)};

  ${mediaQueryUp.desktop} {
    align-items: flex-start;
  }
`

const StatCardsWrapper = styled.div`
  display: none;

  ${mediaQueryUp.desktop} {
    display: flex;
    flex-direction: column;
    gap: ${rem(24)};
  }

`

const MobileStatCards = styled.div`
  display: block;

  ${mediaQueryUp.desktop} {
    display: none;
  }
`

const Footnote = styled(Caption)`
  opacity: 0.8;
  max-width: ${rem(300)};
  margin: 0 auto;
  text-align: center;

  ${mediaQueryUp.desktop} {
    max-width: none;
  }
`

const HeroSheet = styled(Sheet)`
  background: linear-gradient(90deg, ${props => props.theme.palette.product.luxPlus.background} 0%, #4C82DF 100%);
`

const NavSection = styled(Pane)`
  position: sticky;
  z-index: ${zIndex.stickyHeaderBanner};
  top: 0;
  padding-block: ${rem(32)} ${rem(20)};
  padding-inline: 0;
`

const TabContentSpacer = styled.div`
  height: ${rem(32)};
`

const STICKY_NAV_SCROLL_OFFSET = -100
const NON_MEMBER_TABS: ReadonlyArray<LuxPlusLandingNonMemberTab> = ['about', 'benefits', 'partner-offers']

function LuxPlusNonMemberLandingPageContent() {
  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const { currentRegionCode } = useContext(GeoContext)
  const saveAmount = getRegionSaveAmount(currentRegionCode)
  const isRebrand = useRebrandVersion() === 'rebrand'
  const darkCtaClassName = isRebrand ? getThemeClassName({ mobile: 'default' }) : undefined
  const darkCtaVariant = isRebrand ? 'dark' : undefined

  const dispatch = useAppDispatch()
  const [stickyRef, notSticky] = useInView()
  const { activeTab, navigateToTab } = useLuxPlusLandingTab(NON_MEMBER_TABS, 'about')

  const isLoggedIn = useAppSelector(selectLoggedIn)
  const firstName = useAppSelector(state => state.auth.account.givenName)
  const hasSubscriptionOffers = useAppSelector(state => !!state.luxPlus.products.subscription.offers)
  const memberId = useAppSelector(state => state.auth.account.memberId)
  const totalPotentialSavings = useAppSelector(state => state.luxPlus.totalPotentialSavings.data)
  const savingsItems: Array<App.LuxPlusPotentialSavingsItem> = totalPotentialSavings?.items ?? EmptyArray
  const { totalAmount: personalSaving, hasFlightSavings, fetching: isSavingsRowsFetching } = usePotentialSavingsRows(savingsItems)
  const { showPersonalised } = checkPersonalisedSaving(firstName, personalSaving, currentRegionCode)
  // Keep the stat card in its loading state until past-savings data settles, so a logged-in
  // member doesn't briefly see the generic "most members save" card before their personalised one.
  const isTotalSavingsPending = useAppSelector(
    state => !!state.luxPlus.totalPotentialSavings.initial || !!state.luxPlus.totalPotentialSavings.fetching,
  )
  const isPastSavingsLoading = !!memberId && (isTotalSavingsPending || isSavingsRowsFetching)

  // Re-run once authenticated: fetchTotalPotentialSavings bails without a memberId, and on a cold
  // landing the customer often isn't logged in yet at first mount. Depending on memberId re-fires
  // the fetch when it arrives, so past-savings data (and the CTA) loads without needing to navigate
  // away and back.
  useEffect(() => {
    dispatch(fetchTotalPotentialSavings())
  }, [dispatch, memberId])

  const handleTabPartnerOffers = useCallback(() => {
    navigateToTab('partner-offers')
    requestAnimationFrame(() => {
      scrollToId('partner_offers_list', STICKY_NAV_SCROLL_OFFSET)
    })
  }, [navigateToTab])

  const handleTabChange = useCallback<SegmentedOptionsGroupOnValueChangeHandler<LuxPlusLandingNonMemberTab>>((tab) => {
    if (tab === 'partner-offers') {
      handleTabPartnerOffers()
      return
    }
    navigateToTab(tab)
  }, [handleTabPartnerOffers, navigateToTab])

  const hasJoinFeeFreeOffer = useAppSelector(
    state => !!state.luxPlus.products.subscription.offers?.some(o => o.joinFeeWaived),
  )

  const handleSignIn = useCallback(() => {
    showModal<AccountAccessModalResult>(<AccountAccessModal />)
  }, [showModal])

  const handlePastBookingsClick = useCallback(() => {
    // Keep the breakdown accessible when there are flight savings.
    if (personalSaving > 0 || hasFlightSavings) {
      showModal(<LuxPlusPotentialSavingsModal items={savingsItems} />)
    }
  }, [showModal, savingsItems, personalSaving, hasFlightSavings])

  const promoBanners = useLuxPlusPromoBanners()
  const partnerOffers = usePartnerPromoOffers()
  const showPartnerOffersTab = partnerOffers.length >= 3

  const heroHeading = intl.formatMessage({
    id: '-kBO3mwLj9gYeaHonUCi',
    defaultMessage: 'Your VIP Travel Club',
    description: 'Hero heading on the LuxPlus logged-off landing page content',
  })

  const heroSubheading = intl.formatMessage({
    id: 'KBxI7TNIw5P-njf71N0U',
    defaultMessage: 'Unpack a world of member-only benefits.',
    description: 'Hero subheading on LuxPlus logged-off landing page',
  })

  const becomeMemberLabel = intl.formatMessage({
    id: '50G7bLfEyI9VPBij6k5I',
    defaultMessage: 'Become a member',
    description: 'CTA button to start LuxPlus membership purchase on logged-off landing page',
  })

  const becomeMemberNoJoinFeeLabel = intl.formatMessage({
    id: 'kZptru9JkmD5d-3fAtrH',
    defaultMessage: 'Become a member (no joining fee)',
    description: 'Spoof-only CTA button to start LuxPlus membership purchase without joining fee',
  })

  const startFreePreviewLabel = intl.formatMessage({
    id: 'KkElsn1BXHWJJpgNb0iA',
    defaultMessage: 'Start free preview',
    description: 'CTA button to start a free LuxPlus preview on logged-off landing page',
  })

  const signInLabel = intl.formatMessage({
    id: 'uj9GkqTLdw44VTmsd5iy',
    defaultMessage: 'Sign in',
    description: 'Text link for existing members to sign in on LuxPlus logged-off landing page',
  })

  let footnoteText: string
  if (showPersonalised || hasFlightSavings) {
    footnoteText = intl.formatMessage({
      id: 'eCw68lCh8gzdInKwPmH-',
      defaultMessage: '*Based on your actual bookings and {programName} pricing at the time of purchase.',
      description: 'Footnote disclaimer about savings calculation on LuxPlus landing page',
    }, { programName: LUXURY_PLUS.PROGRAM_NAME })
  } else {
    footnoteText = intl.formatMessage({
      id: '4P0voq2Zk_j0v3RegaJU',
      defaultMessage: '*Based on savings by top 10% of active {programName} members over 12 months',
      description: 'Footnote disclaimer about savings calculation on LuxPlus landing page',
    }, { programName: LUXURY_PLUS.PROGRAM_NAME })
  }

  const aboutTabLabel = intl.formatMessage({
    id: '44kT2MggRLwSErniPcPV',
    defaultMessage: 'About',
    description: 'Tab label for the About section on LuxPlus landing page',
  })

  const benefitsTabLabel = intl.formatMessage({
    id: '5SpkviQdtWDk6OcsoqkP',
    defaultMessage: 'Benefits',
    description: 'Tab label for the Benefits section on LuxPlus landing page',
  })

  const partnerOffersTabLabel = intl.formatMessage({
    id: 'R-9iZhfbVJvWCOce6x8A',
    defaultMessage: 'Partner Offers',
    description: 'Tab label for the Partner Offers section on LuxPlus landing page',
  })

  return (
    <>
      <HeroSheet padding="40 10" tabletPadding="40 20" paletteType="inverse">
        <HeroLayoutContainer>
          <HeroGrid>
            <LeftColumn>
              <LuxPlusLogo height={20} />
              <HeadingTextBlock variant="heading1" align="center" tabletAlign="start">{heroHeading}</HeadingTextBlock>
              <BodyText variant="medium" align="center" colour="normal-contrast">
                {heroSubheading}
              </BodyText>
              <MobileStatCards>
                <LuxPlusStatCards saveAmount={saveAmount} firstName={firstName} personalSaving={personalSaving} onPastBookingsClick={handlePastBookingsClick} hasFlightSavings={hasFlightSavings} loading={isPastSavingsLoading} />
              </MobileStatCards>
              <CTAContainer className={darkCtaClassName}>
                <LuxPlusLandingCtaButtons
                  becomeMemberLabel={becomeMemberLabel}
                  startFreePreviewLabel={startFreePreviewLabel}
                  becomeMemberDisabled={!hasSubscriptionOffers}
                  includeSpoofNoJoinFee={hasJoinFeeFreeOffer}
                  spoofNoJoinFeeLabel={becomeMemberNoJoinFeeLabel}
                  gap={12}
                  variant={darkCtaVariant}
                />
              </CTAContainer>
              {!isLoggedIn && <BodyText variant="medium" colour="normal-contrast">
                <FormattedMessage
                  id="R2Yg1UFqRkOjkj05Z93W"
                  defaultMessage="Already a {programName} member? {signInLink}"
                  description="Prompt for existing LuxPlus members to sign in on logged-off landing page"
                  values={{
                    programName: LUXURY_PLUS.PROGRAM_NAME,
                    signInLink: <TextLink onClick={handleSignIn}>{signInLabel}</TextLink>,
                  }}
                />
              </BodyText>}
              <CSSBreakpoint max="tablet">
                <Footnote variant="medium" colour="normal-contrast">
                  {footnoteText}
                </Footnote>
              </CSSBreakpoint>
            </LeftColumn>
            <StatCardsWrapper>
              <LuxPlusStatCards saveAmount={saveAmount} firstName={firstName} personalSaving={personalSaving} onPastBookingsClick={handlePastBookingsClick} hasFlightSavings={hasFlightSavings} loading={isPastSavingsLoading} />
              <CSSBreakpoint min="mobile">
                <Footnote variant="medium" colour="normal-contrast">
                  {footnoteText}
                </Footnote>
              </CSSBreakpoint>
            </StatCardsWrapper>
          </HeroGrid>
        </HeroLayoutContainer>
      </HeroSheet>

      {promoBanners.length > 0 && <LuxPlusPromoBannerCarousel items={promoBanners} />}

      <NavSection shadow={!notSticky ? 'bottom-small' : undefined}>
        <LayoutContainer variant="medium">
          <Group direction="horizontal" horizontalAlign="center">
            <SegmentedOptionsGroup
              inputsName="luxPlusNonMemberLandingTab"
              onValueChange={handleTabChange}
            >
              <SegmentedOption
                checked={activeTab === 'about'}
                value="about"
                title={aboutTabLabel}
              />
              <SegmentedOption
                checked={activeTab === 'benefits'}
                value="benefits"
                title={benefitsTabLabel}
              />
              {showPartnerOffersTab && <SegmentedOption
                checked={activeTab === 'partner-offers'}
                value="partner-offers"
                title={partnerOffersTabLabel}
              />}
            </SegmentedOptionsGroup>
          </Group>
        </LayoutContainer>
      </NavSection>
      <div ref={stickyRef} />

      <TabContentSpacer />
      <LuxPlusLandingNavigationProvider navigateToPartnerOffers={handleTabPartnerOffers} scrollOffset={STICKY_NAV_SCROLL_OFFSET}>
        {activeTab === 'about' && <LuxPlusLandingAboutSection />}
        {activeTab === 'benefits' && <LuxPlusLandingBenefitsSection />}
        {activeTab === 'partner-offers' && showPartnerOffersTab && <LuxPlusLandingPartnerOffersTabSection />}
      </LuxPlusLandingNavigationProvider>
    </>
  )
}

export default LuxPlusNonMemberLandingPageContent
